
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Clock, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ApprovalPost } from "@/types/approval";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BrandLogo } from "@/components/BrandLogo";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

export default function ApprovalView() {
  const { slug } = useParams<{ slug: string }>();
  const [approval, setApproval] = useState<ApprovalPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [userLogo, setUserLogo] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [showRejectFeedback, setShowRejectFeedback] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Load approval data with error handling
  useEffect(() => {
    const fetchApproval = async () => {
      if (!slug) {
        console.log("No slug provided");
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log("Fetching approval with slug:", slug);
        
        // Fetch post from database using slug
        const { data: approvalData, error } = await supabase
          .from('approval_posts')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching approval post:", error);
          toast.error("Erro ao carregar dados");
          setLoading(false);
          return;
        }
        
        if (!approvalData) {
          console.log("No approval found for slug:", slug);
          setLoading(false);
          return;
        }

        console.log("Approval data found:", approvalData);
        
        // Format data to match ApprovalPost type
        const approval: ApprovalPost = {
          id: approvalData.id,
          title: approvalData.title,
          client_name: approvalData.client_name,
          image_url: approvalData.image_url,
          status: approvalData.status as 'pending' | 'approved' | 'rejected' | 'archived',
          feedback: approvalData.feedback,
          platform: approvalData.platform,
          shareable_link: approvalData.shareable_link,
          slug: approvalData.slug,
          created_at: approvalData.created_at,
          updated_at: approvalData.updated_at,
          caption: approvalData.caption,
          post_date: approvalData.post_date,
          instagram_handle: approvalData.instagram_handle,
          post_type: approvalData.post_type as 'static' | 'carousel' | 'reels',
          static_image: approvalData.static_image || approvalData.image_url,
          carousel_images: approvalData.carousel_images || null,
          reels_video: approvalData.reels_video || null
        };
        
        setApproval(approval);
        
        if (approvalData.user_id) {
          console.log("Found user_id:", approvalData.user_id);
          
          // Fetch the user's branding information
          const { data: brandingData, error: brandingError } = await supabase
            .from('user_branding')
            .select('logo_url')
            .eq('user_id', approvalData.user_id)
            .maybeSingle();
            
          if (brandingError) {
            console.error("Error fetching branding:", brandingError);
          }
          
          // Fetch the user's name/agency name
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('agency_name')
            .eq('id', approvalData.user_id)
            .maybeSingle();
            
          if (userError) {
            console.error("Error fetching user data:", userError);
          }
          
          if (brandingData?.logo_url) {
            console.log("Setting user logo:", brandingData.logo_url);
            setUserLogo(brandingData.logo_url);
          }
          
          if (userData?.agency_name) {
            console.log("Setting agency name:", userData.agency_name);
            setAgencyName(userData.agency_name);
          }
        }
      } catch (error) {
        console.error("Error fetching approval:", error);
        toast.error("Erro ao carregar dados de aprovação");
      } finally {
        setLoading(false);
      }
    };
    
    fetchApproval();
  }, [slug]);
  
  // Handle approval submission
  const handleApprove = async () => {
    try {
      if (!approval?.id) {
        toast.error("ID de aprovação não encontrado");
        return;
      }
      
      setSubmitting(true);
      
      // Update approval status in the database
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          status: 'approved', 
          feedback,
          updated_at: new Date().toISOString() 
        })
        .eq('id', approval.id);
      
      if (error) throw error;
      
      toast.success("Post aprovado com sucesso!");
      setApproval(prev => prev ? { ...prev, status: 'approved', feedback } : null);
    } catch (error) {
      console.error("Error approving post:", error);
      toast.error("Erro ao aprovar post");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle rejection submission
  const handleReject = async () => {
    if (!feedback.trim()) {
      toast.error("Por favor, informe o motivo da rejeição");
      return;
    }
    
    if (!approval?.id) {
      toast.error("ID de aprovação não encontrado");
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Update approval status in the database
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          status: 'rejected', 
          feedback,
          updated_at: new Date().toISOString() 
        })
        .eq('id', approval.id);
      
      if (error) throw error;
      
      toast.success("Feedback enviado com sucesso!");
      setApproval(prev => prev ? { ...prev, status: 'rejected', feedback } : null);
    } catch (error) {
      console.error("Error rejecting post:", error);
      toast.error("Erro ao enviar feedback");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject button click
  const handleRejectClick = () => {
    setShowRejectFeedback(true);
  };

  // Render content based on loading and approval state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg">Carregando...</p>
        </div>
      </div>
    );
  }
  
  if (!approval) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <X className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-4">Link de aprovação não encontrado</h1>
          <p className="text-muted-foreground mb-6">
            Este link pode ter expirado ou a aprovação foi arquivada.
            Entre em contato com a agência para mais informações.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900/30">
      {/* Dynamic Logo Header */}
      <header className="w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
          <div className="text-center">
            {userLogo ? (
              <div className="flex justify-center items-center">
                <img 
                  src={userLogo} 
                  alt={agencyName || "Agency Logo"} 
                  className="h-10 object-contain"
                />
                {agencyName && (
                  <span className="ml-2 text-lg font-medium">{agencyName}</span>
                )}
              </div>
            ) : (
              <h2 className="text-lg font-medium">{agencyName || "Aprovação de Conteúdo"}</h2>
            )}
          </div>
        </div>
      </header>
      
      <div className="flex-1 py-8 px-4 md:px-0">
        <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold">{approval.title}</h1>
                <p className="text-muted-foreground">Cliente: {approval.client_name}</p>
                {approval.instagram_handle && (
                  <p className="text-sm text-muted-foreground mt-1">
                    @{approval.instagram_handle}
                  </p>
                )}
              </div>
              
              <div>
                {approval.status === "pending" ? (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                    <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                    <span className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">Aguardando aprovação</span>
                  </div>
                ) : approval.status === "approved" ? (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                    <span className="text-sm text-green-700 dark:text-green-400 font-medium">Aprovado</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <X className="h-4 w-4 text-red-600 dark:text-red-400 mr-2" />
                    <span className="text-sm text-red-700 dark:text-red-400 font-medium">Rejeitado</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 text-sm text-muted-foreground">
              {approval.post_date ? (
                <p>Data de publicação: {format(new Date(approval.post_date), "dd 'de' MMMM, yyyy", { locale: ptBR })}</p>
              ) : (
                <p>Data da aprovação: {format(new Date(approval.created_at), "dd 'de' MMMM, yyyy", { locale: ptBR })}</p>
              )}
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            {/* Display media based on post type */}
            {approval.post_type === "static" && approval.static_image && (
              <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden max-w-md mx-auto mb-8">
                <AspectRatio ratio={4/5}>
                  <img
                    src={approval.static_image}
                    alt={approval.title}
                    className="w-full h-full object-contain"
                  />
                </AspectRatio>
              </div>
            )}
            
            {approval.post_type === "carousel" && approval.carousel_images && approval.carousel_images.length > 0 && (
              <div className="mb-8">
                <Carousel className="w-full max-w-md mx-auto">
                  <CarouselContent>
                    {approval.carousel_images.map((image, index) => (
                      <CarouselItem key={index} className="basis-full">
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden p-1">
                          <AspectRatio ratio={4/5}>
                            <img
                              src={image}
                              alt={`Imagem ${index + 1}`}
                              className="w-full h-full object-contain"
                            />
                          </AspectRatio>
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs rounded-full px-2 py-1">
                            {index + 1}/{approval.carousel_images.length}
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>
            )}
            
            {approval.post_type === "reels" && approval.reels_video && (
              <div className="mb-8 max-w-sm mx-auto">
                <AspectRatio ratio={9/16}>
                  <video 
                    controls 
                    className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded-lg" 
                    src={approval.reels_video}
                    playsInline
                    preload="metadata"
                  ></video>
                </AspectRatio>
              </div>
            )}
            
            {/* Display caption if available */}
            {approval.caption && (
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-2">Legenda:</h3>
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg p-4">
                  <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{approval.caption}</p>
                </div>
              </div>
            )}
            
            {/* Show action buttons when pending */}
            {approval.status === "pending" ? (
              <div className="space-y-6">
                {!showRejectFeedback ? (
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      variant="destructive" 
                      onClick={handleRejectClick}
                      disabled={submitting}
                      className="sm:order-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                    <Button 
                      variant="default"
                      onClick={handleApprove}
                      disabled={submitting}
                      className="sm:order-2 bg-green-600 hover:bg-green-700"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      Aprovar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="feedback" className="block text-sm font-medium mb-2">
                        Motivo da rejeição:
                      </label>
                      <Textarea 
                        id="feedback"
                        placeholder="Digite o motivo da rejeição..."
                        className="min-h-[120px]"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                      />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowRejectFeedback(false);
                          setFeedback("");
                        }}
                        disabled={submitting}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleReject}
                        disabled={submitting || !feedback.trim()}
                      >
                        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
                        Confirmar Rejeição
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : approval.status === "approved" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-green-700 dark:text-green-400 mb-2">Post Aprovado!</h2>
                <p className="text-gray-600 dark:text-gray-400">
                  O conteúdo foi aprovado e será publicado conforme programado.
                </p>
                {approval.feedback && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Feedback:</strong> {approval.feedback}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 rounded-lg border p-4 bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                {approval.feedback ? (
                  <>
                    <h3 className="font-medium mb-2">Motivo da rejeição:</h3>
                    <p className="text-gray-700 dark:text-gray-300">{approval.feedback}</p>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground">
                    Conteúdo rejeitado sem comentários.
                  </p>
                )}
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  <p>
                    Este conteúdo foi rejeitado e não será postado. Um novo conteúdo poderá ser enviado para aprovação.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <footer className="w-full py-4 px-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="text-sm text-muted-foreground">
            {agencyName ? `© ${new Date().getFullYear()} ${agencyName}` : null}
          </div>
          <div className="text-sm">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.location.href = "mailto:contato@seuemail.com"}
              className="h-7 px-2 text-muted-foreground hover:text-foreground"
            >
              Precisa de ajuda? Entre em contato
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
